import { useRef, useState, type ChangeEvent, type ClipboardEvent } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type {
  ApplicationTestingTicketCreated,
  NewApplicationTestingTicket,
} from '@/core/models/applicationTesting.models';
import { applicationTestingService } from '@/core/services/applicationTesting';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { Button } from '@/kit/Button';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { ALLOWED_EXTENSIONS, appendValidatedFiles, filesFromClipboard } from './applicationTestingFiles';
import { isRichTextEmpty, ticketEditorConfig } from './applicationTestingText';
import { PriorityPill, priorityOptions, StatusPill, statusOptions } from './applicationTestingStatus';

/** A new ticket always starts Open; the status field lets it be raised straight into another workflow status. */
const DEFAULT_STATUS = statusOptions.find((s) => s.name === 'Open')?.id ?? statusOptions[0].id;

/** Angular `FormValidationService.validateForm`: true when valid, otherwise the mandatory-fields toast. */
function validateForm(isValid: boolean): boolean {
  if (isValid) {
    return true;
  }
  notifyError(NotificationTextMessage.validationMessage);
  return false;
}

type Priority = NewApplicationTestingTicket['priority'];

/**
 * The priority is picked with the same pill the list and ticket page use (shared priorityOptions); the form keeps
 * the priority's name code ("high"/"medium"/"low"), which is what the create request sends.
 */
const codeOfPriority = (id: number): Priority =>
  (priorityOptions.find((p) => p.id === id)?.name.toLowerCase() ?? 'low') as Priority;
const idOfPriority = (code: Priority): number =>
  priorityOptions.find((p) => p.name.toLowerCase() === code)?.id ?? priorityOptions[priorityOptions.length - 1].id;

interface TicketForm {
  subject: string;
  description: string;
  priority: Priority;
  status: number;
}

const emptyForm: TicketForm = { subject: '', description: '', priority: 'low', status: DEFAULT_STATUS };

type TextField = 'subject' | 'description';

const DESCRIPTION_EDITOR = ticketEditorConfig(
  'Describe the problem: what you did, what you expected, what happened.',
  '160',
);

/** subject required (spaces only is empty; max 255, also capped while typing); description required (HTML from the editor). */
function isFieldInvalid(field: TextField, value: string): boolean {
  if (field === 'description') {
    return isRichTextEmpty(value);
  }
  return value.trim().length === 0 || value.length > 255;
}

/**
 * Raise a ticket asking for the app itself to be improved (a separate feature from support tickets, its own
 * fields and its own attachments). Unlike AddSupportTicket - which leaves the success toast to its caller because
 * it is also opened from the ticket list, which refreshes itself - this dialog has no list screen behind it, so it
 * toasts and closes itself on success.
 */
export function RaiseApplicationTesting({ dialogRef }: DialogComponentProps<unknown, ApplicationTestingTicketCreated>) {
  const [form, setForm] = useState<TicketForm>(emptyForm);
  const [files, setFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  /** ng-touched: fields the user left, or every field after a failed submit. */
  const [touched, setTouched] = useState<Set<TextField>>(new Set());

  const set = <K extends keyof TicketForm>(key: K, value: TicketForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const touch = (field: TextField) => setTouched((current) => new Set(current).add(field));
  const invalid = (field: TextField) => touched.has(field) && isFieldInvalid(field, form[field]);
  const message = (field: TextField) =>
    invalid(field) ? <span className="text-red font-size-12">This field is required.</span> : null;

  const onFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const incoming = Array.from(input.files ?? []);
    input.value = '';
    setFiles((current) => appendValidatedFiles(current, incoming));
  };

  const removeFile = (index: number) => setFiles((current) => current.filter((_, i) => i !== index));

  /** Ctrl+V anywhere in the dialog attaches a pasted screenshot or copied file the same way the file picker does. */
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pasted = filesFromClipboard(event);
    if (pasted.length === 0) return;
    event.preventDefault();
    setFiles((current) => appendValidatedFiles(current, pasted));
  };

  const onSubmit = () => {
    if (saving.current) {
      return;
    }
    const isValid = !isFieldInvalid('subject', form.subject) && !isFieldInvalid('description', form.description);
    if (!validateForm(isValid)) {
      setTouched(new Set<TextField>(['subject', 'description']));
      return;
    }
    saving.current = true;
    setIsSaving(true);
    applicationTestingService
      .saveTicket({
        subject: form.subject,
        description: form.description,
        priority: form.priority,
        status: form.status,
        files,
      })
      .then(
        (created) => {
          notifySuccess(created.message ?? 'Ticket raised successfully');
          dialogRef.close(created);
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        saving.current = false;
        setIsSaving(false);
      });
  };

  return (
    // tabIndex lets the dialog itself take focus when clicked, so Ctrl+V reaches onPaste even when no field is focused.
    <div className="mat-dialog-wrapper application-testing-popup" tabIndex={-1} onPaste={onPaste}>
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Application Testing</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="d-flex gap-20 mb-1">
              <div className="form-field application-testing-subject w-434">
                <p className="fw-bold">
                  Subject <span className="text-red">*</span>
                </p>
                <TextareaField
                  rows={2}
                  fieldClassName="wp-100"
                  maxLength={255}
                  value={form.subject}
                  invalid={invalid('subject')}
                  // one line of meaning, however it wraps: a pasted line break becomes a space
                  onChange={(event) => set('subject', event.target.value.replace(/\s*[\r\n]+\s*/g, ' '))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  onBlur={() => touch('subject')}
                />
                {message('subject')}
              </div>
              <div className="form-field w-230 application-testing-raise-priority">
                <p className="fw-bold">Priority</p>
                <PriorityPill
                  value={idOfPriority(form.priority)}
                  onChange={(id) => set('priority', codeOfPriority(id))}
                />
              </div>
              <div className="form-field w-230 application-testing-raise-status">
                <p className="fw-bold">Status</p>
                <StatusPill value={form.status} onChange={(id) => set('status', id)} />
              </div>
            </div>
            <div>
              <div className="form-field textarea mb-1">
                <p className="fw-bold">
                  Description <span className="text-red">*</span>
                </p>
                <div
                  className={`application-testing-editor${invalid('description') ? ' invalid' : ''}`}
                  onBlur={() => touch('description')}
                >
                  <RichTextEditor
                    id="applicationTestingDescription"
                    value={form.description}
                    config={DESCRIPTION_EDITOR}
                    onChange={(html) => set('description', html)}
                  />
                </div>
                {message('description')}
              </div>
              <div className="form-field attached">
                <p className="fw-bold">Attachments</p>
                <div className="form-field attached mb-10">
                  <div className="attachFile">
                    <Icon name="cloud_upload" outlined />
                    <input
                      multiple
                      type="file"
                      autoComplete="off"
                      maxLength={100}
                      id="application-testing-file"
                      accept={ALLOWED_EXTENSIONS.join(',')}
                      onChange={onFilesSelected}
                      className="cursor-pointer"
                    />
                    <label htmlFor="application-testing-file" className="cursor-pointer">
                      <strong>Choose a file</strong>, drag it here or paste it (Ctrl+V) to attach a file with ticket.
                    </label>
                  </div>
                </div>
                <span className="font-size-12">
                  (Max file size - 2 MB) | Supported formats : .doc, .docx, .pdf, .jpg, .png, .jpeg, .txt, .xls, .xlsx,
                  .csv
                </span>
                {files.length > 0 && (
                  <div className="note-table mt-10">
                    <div className="table-border">
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${index}`}
                          className="clickable d-flex align-items-center justify-space-between"
                        >
                          <div>{file.name}</div>
                          <div className="close mt-5">
                            <a
                              href="#"
                              aria-label={`Remove ${file.name}`}
                              onClick={(event) => {
                                event.preventDefault();
                                removeFile(index);
                              }}
                            >
                              <Icon name="close" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSubmit}>
              Submit
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
